import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

export interface Mission {
  type: string;
  label: string;
  emoji: string;
  points: number;
  completed: boolean;
}

const MISSION_DEFS: Omit<Mission, "completed">[] = [
  { type: "share_product", label: "שתף מוצר", emoji: "🔗", points: 5 },
  { type: "write_review", label: "כתוב ביקורת", emoji: "⭐", points: 10 },
];

export function useDailyMissions() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: completedMissions = [], isLoading } = useQuery({
    queryKey: ["daily-missions", user?.id],
    queryFn: async () => {
      const today = new Date().toISOString().split("T")[0];
      const { data, error } = await supabase
        .from("daily_missions" as any)
        .select("mission_type, completed")
        .eq("user_id", user!.id)
        .eq("mission_date", today)
        .eq("completed", true);
      if (error) throw error;
      return (data as any[])?.map((d: any) => d.mission_type as string) ?? [];
    },
    enabled: !!user,
    staleTime: 30_000,
  });

  const missions: Mission[] = MISSION_DEFS.map((def) => ({
    ...def,
    completed: completedMissions.includes(def.type),
  }));

  const completeMission = useMutation({
    mutationFn: async (missionType: string) => {
      const { data, error } = await supabase.rpc("complete_daily_mission" as any, {
        _user_id: user!.id,
        _mission_type: missionType,
      });
      if (error) throw error;
      return data as any;
    },
    onSuccess: (result, missionType) => {
      if (result?.already_completed) return;
      const def = MISSION_DEFS.find((m) => m.type === missionType);
      toast.success(`${def?.emoji} משימה הושלמה! +${result?.points_awarded ?? def?.points} נקודות`);
      queryClient.invalidateQueries({ queryKey: ["daily-missions"] });
      queryClient.invalidateQueries({ queryKey: ["points"] });
    },
  });

  const allCompleted = missions.every((m) => m.completed);
  const completedCount = missions.filter((m) => m.completed).length;

  return {
    missions,
    isLoading,
    completeMission: completeMission.mutateAsync,
    allCompleted,
    completedCount,
    totalCount: missions.length,
  };
}
